import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { WebSocketService } from './websocket/WebSocketService';
import { GeneralService } from './general.service';
import { environment } from '@src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserStatusService {
  private userStatusSubject = new BehaviorSubject<Map<number, string>>(
    new Map()
  );
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private messageSubscription: Subscription | null = null;
  private connectionSubscription: Subscription | null = null;
  private static instance: UserStatusService;
  private lastStatusUpdate = new Map<
    number,
    { status: string; timestamp: number }
  >();
  private readonly STATUS_UPDATE_THROTTLE = 30000; // 30 segundos entre actualizaciones para reducir spam
  private isInitialized = false;

  // 🔇 Contadores para logs silenciosos
  private silentLogCounters = {
    individual: 0,
    all: 0,
    mass_update: 0,
    activity: 0,
  };

  constructor(
    private websocketService: WebSocketService,
    private generalService: GeneralService,
    private http: HttpClient
  ) {
    if (UserStatusService.instance) {
      return UserStatusService.instance;
    }
    UserStatusService.instance = this;
    
    // ⚠️ NO inicializar automáticamente - solo cuando se necesite explícitamente
    console.log('🔧 UserStatusService creado pero NO inicializado automáticamente');
  }

  /**
   * Conecta solo al usuario actual sin inicializar todo el servicio
   * Para uso en componentes que solo necesitan mostrar su propio estado
   * ⚠️ NO inicializa suscripciones a todos los usuarios
   */
  connectCurrentUser(): void {
    const userId = this.generalService.getUserId();
    if (userId) {
      console.log('👤 Conectando SOLO usuario actual (sin suscripciones masivas):', userId);
      this.connectUser(userId);
      
      // ⚠️ NO llamar a initialize() aquí para evitar suscripciones innecesarias
      // Solo conectar al usuario actual
    }
  }

  /**
   * Inicializa el servicio de estado de usuarios
   * Solo debe llamarse desde páginas que necesiten gestionar estados de usuarios (como user-list)
   */
  initialize(): void {
    console.log('🚀 Inicializando UserStatusService para gestión de usuarios');
    this.initSubscriptions();

    // NO conectar automáticamente - eso se hace en el login
    // this.connectCurrentUser();

    // Solicitar estados iniciales de todos los usuarios
    this.requestUserStatuses();
  }

  /**
   * Inicializa las suscripciones a WebSocket
   */
  private initSubscriptions(): void {
    if (this.isInitialized) {
      return;
    }

    this.clearSubscriptions();
    this.isInitialized = true;

    // Suscribirse a los topics específicos de estado de usuarios
    this.messageSubscription = this.websocketService
      .getMessagesByDestination('/topic/users/status')
      .subscribe((userStatus) => {
        // 🔇 Log silencioso - solo para debugging crítico
        this.logSilentStatusUpdate('individual', userStatus);
        this.updateUserStatus(userStatus);
      });

    // Suscribirse a la lista completa de estados
    const allStatusSubscription = this.websocketService
      .getMessagesByDestination('/topic/users/status/all')
      .subscribe((allStatuses) => {
        // 🔇 Log silencioso - solo para debugging crítico
        this.logSilentStatusUpdate('all', allStatuses);
        this.updateUserStatus(allStatuses);
      });

    // Suscribirse a notificaciones generales
    const notificationsSubscription = this.websocketService
      .getMessagesByDestination('/topic/notifications')
      .subscribe((notification) => {
        if (notification.type === 'USERS_STATUS_UPDATED') {
          // 🔇 Log silencioso para actualizaciones masivas
          this.logSilentStatusUpdate('mass_update', notification.payload);
          this.updateUserStatus(notification.payload);
        } else if (notification.type === 'USER_ACTIVITY_UPDATED') {
          // 🔇 Log silencioso para actividad
          this.logSilentStatusUpdate('activity', notification);
          // Actualizar el estado del usuario específico
          this.updateUserStatus({
            userId: notification.userId,
            status: notification.status,
          });
        } else if (
          notification.type === 'USER_CONNECTED' ||
          notification.type === 'USER_DISCONNECTED'
        ) {
          // 🔇 Log silencioso para conexiones/desconexiones individuales
          this.logSilentStatusUpdate('individual', notification);
          // Actualizar el estado del usuario específico
          this.updateUserStatus({
            userId: notification.userId,
            username: notification.username,
            nombre: notification.nombre,
            apellido: notification.apellido,
            status: notification.online ? 'ONLINE' : 'OFFLINE',
            online: notification.online,
          });
        }
      });

    // Agregar las suscripciones adicionales a la limpieza
    this.clearSubscriptions = () => {
      if (this.messageSubscription) {
        this.messageSubscription.unsubscribe();
        this.messageSubscription = null;
      }
      if (allStatusSubscription) {
        allStatusSubscription.unsubscribe();
      }
      if (notificationsSubscription) {
        notificationsSubscription.unsubscribe();
      }
      if (this.connectionSubscription) {
        this.connectionSubscription.unsubscribe();
        this.connectionSubscription = null;
      }
    };

    this.connectionSubscription = this.websocketService
      .getConnectionStatus()
      .subscribe((connected) => {
        this.connectionStatusSubject.next(connected);
        if (!connected) {
          this.markAllUsersOffline();
        } else if (!window.location.href.includes('/auth/login')) {
          this.requestUserStatuses();
        }
      });
  }

  /**
   * 🔇 Log silencioso para actualizaciones de estado (reduce spam de logs)
   */
  private logSilentStatusUpdate(
    type: keyof typeof this.silentLogCounters,
    data: any
  ): void {
    this.silentLogCounters[type]++;

    // Solo mostrar log cada 500 actualizaciones para reducir spam significativamente
    if (this.silentLogCounters[type] % 500 === 0) {
      const count = this.silentLogCounters[type];
      const dataSize = Array.isArray(data) ? data.length : 1;
      // console.log(
      //   `🔇 ${count} actualizaciones de estado procesadas silenciosamente (${type}, último: ${dataSize} usuarios)`
      // );
    }
  }

  private handleUserDisconnection(userId: number): void {
    const currentMap = this.userStatusSubject.value;
    if (currentMap.has(userId)) {
      const newMap = new Map(currentMap);
      newMap.set(userId, 'OFFLINE');
      this.userStatusSubject.next(newMap);

      const currentUserId = this.generalService.getUserId();
      if (currentUserId && userId === currentUserId) {
        localStorage.setItem('userStatus', 'OFFLINE');
      }
    }
  }

  private updateUserStatus(userStatus: any): void {
    const currentMap = this.userStatusSubject.value;
    const newMap = new Map(currentMap);
    let hasChanges = false;
    const currentUserId = this.generalService.getUserId();
    let currentUserUpdated = false;

    // 🔇 Solo mostrar logs para el usuario actual o en debugging crítico
    const isDebugMode = false; // Cambiar a true solo para debugging

    if (isDebugMode) {
      // console.log('🔄 updateUserStatus llamado:');
      // console.log('  - Usuario actual:', currentUserId);
      // console.log(
      //   '  - Datos recibidos:',
      //   Array.isArray(userStatus)
      //     ? `${userStatus.length} usuarios`
      //     : '1 usuario'
      // );
    }

    const processStatus = (status: any) => {
      const userId = status.userId;
      const normalizedStatus = this.normalizeStatus(status);
      const lastUpdate = this.lastStatusUpdate.get(userId);
      const now = Date.now();

      // 🔇 Solo mostrar logs para el usuario actual
      if (userId === currentUserId) {
        console.log(
          '  ⚠️ ACTUALIZANDO USUARIO ACTUAL:',
          userId,
          'a estado:',
          normalizedStatus
        );
        currentUserUpdated = true;
      } else if (isDebugMode) {
        console.log(
          '  - Procesando usuario:',
          userId,
          'Estado:',
          normalizedStatus
        );
      }

      if (
        !lastUpdate ||
        lastUpdate.status !== normalizedStatus ||
        now - lastUpdate.timestamp > this.STATUS_UPDATE_THROTTLE
      ) {
        newMap.set(userId, normalizedStatus);
        this.lastStatusUpdate.set(userId, {
          status: normalizedStatus,
          timestamp: now,
        });
        hasChanges = true;
      }
    };

    if (Array.isArray(userStatus)) {
      userStatus.forEach(processStatus);
    } else {
      processStatus(userStatus);
    }

    if (hasChanges) {
      this.userStatusSubject.next(newMap);
      this.updateLocalStorage(newMap, currentUserUpdated);
    }
  }

  private normalizeStatus(status: any): string {
    return status.status === 'ONLINE' || status.online === true
      ? 'ONLINE'
      : 'OFFLINE';
  }

  private updateLocalStorage(
    statusMap: Map<number, string>,
    currentUserUpdated: boolean = false
  ): void {
    const currentUserId = this.generalService.getUserId();
    if (currentUserId) {
      const currentUserStatus = statusMap.get(currentUserId) || 'OFFLINE';
      const previousStatus = localStorage.getItem('userStatus');

      // 🔇 Solo mostrar logs si el usuario actual fue actualizado o en debugging crítico
      const isDebugMode = false; // Cambiar a true solo para debugging

      if (currentUserUpdated || isDebugMode) {
        console.log('🔄 updateLocalStorage llamado:');
        console.log('  - Usuario ID:', currentUserId);
        console.log('  - Estado anterior:', previousStatus);
        console.log('  - Estado nuevo:', currentUserStatus);

        // 🔇 Eliminar stack trace - genera mucho ruido
        if (isDebugMode) {
          console.log('  - Stack trace:', new Error().stack);
        }
      }

      localStorage.setItem('userStatus', currentUserStatus);

      // Disparar evento para que los componentes se actualicen
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: 'userStatus',
          newValue: currentUserStatus,
          oldValue: previousStatus,
        })
      );
    }
  }

  /**
   * Limpia las suscripciones existentes
   */
  private clearSubscriptions(): void {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
      this.messageSubscription = null;
    }

    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.connectionSubscription = null;
    }
  }

  /**
   * Solicita el estado actual de todos los usuarios usando HTTP
   */
  requestUserStatuses(): void {
    // Verificar si estamos en la página de login
    if (window.location.href.includes('/auth/login')) {
      return;
    }

    // Verificar si hay token antes de solicitar el estado de los usuarios
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    console.log('📋 Solicitando estados de usuarios por HTTP');

    // Usar el endpoint HTTP para obtener usuarios con estado de conexión
    const url = `${environment.url}api/user/listar?page=0&size=10&soloConectados=false`;

    this.http.get<any>(url).subscribe({
      next: (response) => {
        if (response && response.data && response.data.content) {
          // 🔇 Log reducido para carga inicial
          console.log(
            '👥 Estados de usuarios cargados por HTTP:',
            response.data.content.length
          );

          // Procesar la respuesta y actualizar el mapa de estados
          const userStatuses = response.data.content.map((user: any) => ({
            userId: user.id,
            username: user.username,
            nombre: user.nombre,
            apellido: user.apellido,
            status: user.online ? 'ONLINE' : 'OFFLINE',
            online: user.online || false, // Usar el campo online del backend
            lastActivity: user.lastActivity,
          }));

          this.updateUserStatus(userStatuses);
        }
      },
      error: (error) => {
        console.error(
          '❌ Error al solicitar estados de usuarios por HTTP:',
          error
        );
      },
    });
  }

  /**
   * Marca a todos los usuarios como desconectados
   */
  private markAllUsersOffline(): void {
    const currentMap = this.userStatusSubject.value;
    const newMap = new Map<number, string>();

    // Copiar el mapa actual pero cambiar todos los estados a OFFLINE
    currentMap.forEach((_, userId) => {
      newMap.set(userId, 'OFFLINE');
    });

    this.userStatusSubject.next(newMap);

    // ⚠️ NO sobrescribir localStorage si estamos en proceso de login
    // o si el usuario actual ya está marcado como ONLINE
    const currentUserStatus = localStorage.getItem('userStatus');

    // Solo marcar como OFFLINE si no estamos en login y el usuario no está ONLINE
    if (
      !window.location.href.includes('/auth/login') &&
      currentUserStatus !== 'ONLINE'
    ) {
      localStorage.setItem('userStatus', 'OFFLINE');
      console.log('🔴 Marcando usuario como OFFLINE por desconexión WebSocket');
    } else {
      console.log(
        '⚠️ NO marcando usuario como OFFLINE - Login en proceso o usuario ya ONLINE'
      );
    }
  }

  /**
   * Conecta un usuario usando HTTP
   * @param userId ID del usuario
   */
  connectUser(userId: number): void {
    const url = `${environment.url}api/user/connect/${userId}`;
    const token = localStorage.getItem('token');
    const options = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};

    this.http.post<any>(url, {}, options).subscribe({
      next: (response) => {
        // 🔇 Solo mostrar log para el usuario actual
        const currentUserId = this.generalService.getUserId();
        if (userId === currentUserId) {
          console.log('✅ Usuario actual conectado por HTTP:', response);
        }

        if (response && response.rpta === 1 && response.data) {
          // Actualizar el estado en el servicio
          this.updateUserStatus({
            userId: response.data.userId,
            username: response.data.username,
            nombre: response.data.nombre,
            apellido: response.data.apellido,
            status: response.data.status,
            online: response.data.status === 'ONLINE',
          });

          // Actualizar localStorage para que el componente lo detecte
          localStorage.setItem('userStatus', response.data.status);

          // Forzar actualización del BehaviorSubject
          const currentMap = this.userStatusSubject.value;
          currentMap.set(userId, response.data.status);
          this.userStatusSubject.next(new Map(currentMap));
        }
      },
      error: (error) => {
        console.error('❌ Error al conectar usuario por HTTP:', error);
      },
    });
  }

  /**
   * Desconecta un usuario usando HTTP
   * @param userId ID del usuario
   */
  disconnectUser(userId: number): void {
    const url = `${environment.url}api/user/disconnect/${userId}`;
    this.http.post<any>(url, {}).subscribe({
      next: (response) => {
        // 🔇 Solo mostrar log para el usuario actual
        const currentUserId = this.generalService.getUserId();
        if (userId === currentUserId) {
          console.log('✅ Usuario actual desconectado por HTTP:', response);
        }

        if (response.rpta === 1 && response.data) {
          this.updateUserStatus({
            userId: response.data.userId,
            username: response.data.username,
            nombre: response.data.nombre,
            apellido: response.data.apellido,
            status: response.data.status,
            online: response.data.status === 'ONLINE',
          });
        }
      },
      error: (error) => {
        console.error('❌ Error al desconectar usuario por HTTP:', error);
      },
    });
  }

  /**
   * Actualiza la actividad de un usuario usando HTTP
   * @param userId ID del usuario
   */
  updateUserActivity(userId: number): void {
    const url = `${environment.url}api/user/activity/${userId}`;
    this.http.post<any>(url, {}).subscribe({
      next: (_response) => {
        // 🔇 Log silencioso para actividad - solo mostrar errores
        // console.log('✅ Actividad actualizada por HTTP:', _response);
      },
      error: (error) => {
        console.error('❌ Error al actualizar actividad por HTTP:', error);
      },
    });
  }

  /**
   * Obtiene el estado de un usuario
   * @param userId ID del usuario
   * @returns Observable con el estado del usuario
   */
  getUserStatus(userId: number): Observable<string> {
    return new Observable((observer) => {
      const currentUserId = this.generalService.getUserId();
      let lastEmittedStatus: string | null = null;

      // ⚠️ Si es el usuario actual, verificar localStorage primero
      let currentStatus: string;
      if (userId === currentUserId) {
        const localStorageStatus = localStorage.getItem('userStatus');
        const mapStatus = this.userStatusSubject.value.get(userId);

        // Priorizar localStorage si existe y es ONLINE
        if (localStorageStatus === 'ONLINE') {
          currentStatus = 'ONLINE';
          // Actualizar el mapa para que esté sincronizado
          const currentMap = this.userStatusSubject.value;
          if (currentMap.get(userId) !== 'ONLINE') {
            currentMap.set(userId, 'ONLINE');
            this.userStatusSubject.next(new Map(currentMap));
          }
        } else {
          currentStatus = mapStatus || 'OFFLINE';
        }
      } else {
        // Para otros usuarios, usar el mapa normalmente
        currentStatus = this.userStatusSubject.value.get(userId) || 'OFFLINE';
      }

      // Emitir el estado actual solo si es diferente al último emitido
      if (lastEmittedStatus !== currentStatus) {
        observer.next(currentStatus);
        lastEmittedStatus = currentStatus;
      }

      // Suscribirse a cambios futuros
      const subscription = this.userStatusSubject.subscribe((map) => {
        const newStatus = map.get(userId) || 'OFFLINE';

        // Para el usuario actual, verificar localStorage antes de emitir OFFLINE
        if (userId === currentUserId && newStatus === 'OFFLINE') {
          const localStorageStatus = localStorage.getItem('userStatus');
          if (localStorageStatus === 'ONLINE') {
            return; // No emitir OFFLINE si localStorage dice ONLINE
          }
        }

        // Solo emitir si el estado ha cambiado
        if (lastEmittedStatus !== newStatus) {
          observer.next(newStatus);
          lastEmittedStatus = newStatus;
        }
      });

      // Limpiar suscripción al desuscribirse
      return () => subscription.unsubscribe();
    });
  }

  /**
   * Obtiene el mapa completo de estados de usuarios
   * @returns Observable con el mapa de estados
   */
  getAllUserStatuses(): Observable<Map<number, string>> {
    return this.userStatusSubject.asObservable();
  }

  /**
   * Obtiene el estado de conexión con el servidor
   * @returns Observable con el estado de conexión
   */
  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatusSubject.asObservable();
  }

  /**
   * Actualiza explícitamente el estado del usuario actual
   * @param online Estado de conexión (true = conectado, false = desconectado)
   */
  updateCurrentUserStatus(online: boolean): void {
    const userId = this.generalService.getUserId();
    if (!userId) {
      return;
    }
    const status = online ? 'ONLINE' : 'OFFLINE';
    const currentStatus = localStorage.getItem('userStatus');

    // ⚠️ NO sobrescribir si ya está ONLINE y estamos pidiendo ONLINE
    if (currentStatus === 'ONLINE' && status === 'ONLINE') {
      // Solo actualizar el mapa interno, no localStorage
      const currentMap = this.userStatusSubject.value;
      currentMap.set(userId, status);
      this.userStatusSubject.next(new Map(currentMap));
      return; // No hacer llamada al servidor
    }
    // Actualizar el estado en localStorage solo si es necesario
    localStorage.setItem('userStatus', status);
    // Actualizar el mapa de estados
    const currentMap = this.userStatusSubject.value;
    currentMap.set(userId, status);
    this.userStatusSubject.next(new Map(currentMap));
    // Enviar mensaje al servidor
    if (this.websocketService.isConnected()) {
      if (online) {
        this.connectUser(userId);
      } else {
        this.disconnectUser(userId);
      }
    }
  }
  /**
   * Limpia las suscripciones al destruir el servicio
   */
  ngOnDestroy(): void {
    this.clearSubscriptions();
  }
}
